import { logClientError } from 'src/util/server-log';
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { SharedService } from "@services/shared/shared.service";
import { GrpcCheckoutService } from "@services/grpc-checkout/grpc-checkout.service";
import { GrpcSubscriptionService } from "@services/grpc-subscription/grpc-subscription.service";
import { from, Observable, of } from "rxjs";
import { switchMap, catchError, map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class CheckoutGuard implements CanActivate {
  constructor(
    private router: Router,
    private shared: SharedService,
    private grpcCheckout: GrpcCheckoutService,
    private grpcSubscription: GrpcSubscriptionService,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const planId = this.shared.getPlanId();
    const user = this.shared.user;
    if (!user) {
      // This should be handled by AuthGuard, but as a safeguard:
      this.router.navigate(["/entrar"]);
      return of(false);
    }

    if (!planId) {
      // If there's no planId, the user has no business being in the checkout flow.
      // Redirect them to their profile.
      this.router.navigate(["/minha-conta"]);
      return of(false);
    }

    // If a valid checkout session for this plan already exists AND the plan is loaded, allow access.
    if (this.shared.checkout?.sessionId && this.shared.checkout?.planId === planId && this.shared.currentPlan) {
      return of(true);
    }

    // If not, we must start a new checkout flow from scratch.
    this.shared.clearCheckout();
    this.shared.setPlanId(planId);

    // 1. First, load the plan data
    return from(this.grpcSubscription.getPlanById(planId)).pipe(
      switchMap((planResponse) => {
        const planData = (planResponse as any).toObject();
        if (planData.plan) {
          this.shared.currentPlan = planData.plan;
        }
        // 2. Create a new checkout session
        return from(this.grpcCheckout.createCheckout(user.id));
      }),
      switchMap((sessionResponse) => {
        const session = sessionResponse.toObject();
        if (session.session) {
          this.shared.sessionId = session.session.id;
          this.grpcCheckout.sessionId = session.session.id;
          this.shared.updateCheckout({ sessionId: session.session.id, planId: planId });
        }
        // 3. Add the item to the session
        return from(this.grpcCheckout.addItem(user.id, planId, 1));
      }),
      map(() => true), // If all operations succeed, allow activation.
      catchError((error) => {
        logClientError("Error in CheckoutGuard:", error);
        // If any error occurs during checkout creation, clear planId and redirect to profile.
        this.shared.setPlanId(null);
        this.router.navigate(["/minha-conta"]);
        return of(false);
      }),
    );
  }
}
